import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPathApi, WordlistsApi } from 'src/api';
import { SavedLearnablesResponse } from 'src/api/userPath/types';
import { SavedLearnableSource, setSavedLearnableSource } from 'src/utils/savedLearnableSources';

export const savedLearnablesQueryKey = (languagePairId: number | null) => [
  'savedLearnables',
  languagePairId,
];

export const savedWordsQueryKey = (languagePairId: number | null) => ['savedWords', languagePairId];

type ToggleVariables = { learnableId: number; save: boolean; source?: SavedLearnableSource };

type SavedLearnablesOptions = {
  onSuccess?: (saved: boolean) => void;
  onError?: () => void;
};

/**
 * Words saved by a blob experience user, shared with My Words → Saved.
 * Saving adds the word to the personal wordlist, unsaving removes it.
 */
export const useSavedLearnables = (
  languagePairId: number | null,
  { onSuccess, onError }: SavedLearnablesOptions = {},
) => {
  const queryClient = useQueryClient();
  const queryKey = savedLearnablesQueryKey(languagePairId);

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => UserPathApi.getSavedLearnables(languagePairId as number),
    enabled: !!languagePairId,
  });
  const savedLearnableIds = useMemo(() => data?.learnableIds ?? [], [data]);
  const savedIds = useMemo(() => new Set(savedLearnableIds), [savedLearnableIds]);

  const {
    mutate,
    isPending: isSaving,
    error: saveError,
  } = useMutation({
    mutationFn: ({ learnableId, save }: ToggleVariables) =>
      save
        ? WordlistsApi.addLearnablesToWordlist(languagePairId as number, [learnableId])
        : WordlistsApi.removeLearnablesFromWordlist(languagePairId as number, [learnableId]),
    onMutate: async ({ learnableId, save }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SavedLearnablesResponse>(queryKey);
      const learnableIds = (previous?.learnableIds ?? []).filter(id => id !== learnableId);
      queryClient.setQueryData<SavedLearnablesResponse>(queryKey, {
        learnableIds: save ? [...learnableIds, learnableId] : learnableIds,
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous ?? { learnableIds: [] });
      onError?.();
    },
    onSuccess: (_data, { learnableId, save, source }) => {
      setSavedLearnableSource(
        languagePairId as number,
        learnableId,
        save ? (source ?? null) : null,
      );
      onSuccess?.(save);
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey }),
        queryClient.invalidateQueries({ queryKey: savedWordsQueryKey(languagePairId) }),
      ]),
  });

  const isSaved = useCallback((learnableId: number) => savedIds.has(learnableId), [savedIds]);
  const toggleSaved = useCallback(
    (learnableId: number, source?: SavedLearnableSource) => {
      if (!languagePairId) return;
      mutate({ learnableId, save: !savedIds.has(learnableId), source });
    },
    [mutate, savedIds, languagePairId],
  );

  return { savedLearnableIds, isLoading, isError, isSaving, saveError, isSaved, toggleSaved };
};
